import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Album, AuthUser, Organization } from '@nargus/shared';
import { useNavigate } from '@tanstack/react-router';
import { Pencil, Share2, Trash2 } from 'lucide-react';
import { useDeleteAlbum } from '@/resources/album/album.service';
import { Button } from '@/components/ui/button';
import { AlbumDialog } from './albums-section';
import { PhotoLibrary } from './photo-library';
import { PhotoShareDialog } from './photo-share-dialog';
import { PhotoTabs } from './photos-section';
import { ConfirmDialog } from './ui';

export function AlbumDetail({
  organization,
  user,
  album,
}: {
  organization: Organization;
  user: AuthUser;
  album: Album;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [renaming, setRenaming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const remove = useDeleteAlbum();

  return (
    <div className="flex flex-col gap-4">
      <PhotoTabs organizationId={organization.id} active="albums" />

      <PhotoLibrary
        organization={organization}
        user={user}
        album={album}
        title={album.name}
        hint={t('albums.count', { count: album.photoCount })}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={t('albums.rename')}
              onClick={() => setRenaming(true)}
            >
              <Pencil />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={t('shares.shareAlbum')}
              onClick={() => setSharing(true)}
            >
              <Share2 />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={t('albums.remove')}
              onClick={() => setRemoving(true)}
            >
              <Trash2 />
            </Button>
          </>
        }
      />

      {renaming && (
        <AlbumDialog
          organization={organization}
          album={album}
          onClose={() => setRenaming(false)}
        />
      )}

      {sharing && (
        <PhotoShareDialog
          organization={organization}
          albumId={album.id}
          onClose={() => setSharing(false)}
        />
      )}

      {removing && (
        <ConfirmDialog
          title={t('albums.removeTitle')}
          description={t('albums.removeHint', { name: album.name })}
          confirmLabel={t('common.delete')}
          busy={remove.isPending}
          error={remove.error}
          onClose={() => setRemoving(false)}
          onConfirm={() =>
            remove.mutate(
              { organizationId: organization.id, albumId: album.id },
              {
                onSuccess: () =>
                  void navigate({
                    to: '/organizations/$organizationId/photos/albums',
                    params: { organizationId: organization.id },
                  }),
              },
            )
          }
        />
      )}
    </div>
  );
}
